/**
 * Minimal i18n layer.
 *
 * We deliberately don't pull in a full library (i18next, react-intl, …) —
 * the widget bundle has to stay small (rule 10 of the coding guidelines).
 * Instead we keep flat string tables per locale and look keys up with a
 * `t()` helper. Locale resolution precedence (highest first):
 *
 *   1. `STORAGE_KEYS.locale` in sessionStorage
 *   2. `data-conny-locale="..."` on the loader <script> tag
 *   3. <html lang="…"> attribute
 *   4. `navigator.language`
 *   5. fallback: "en"
 *
 * Add a new locale by extending the `STRINGS` table; missing keys fall back
 * to English so partial translations are safe.
 */
import { readStorage, STORAGE_KEYS, useStorage } from "./storage";

export type Locale = "en" | "de";

const SUPPORTED: Locale[] = ["en", "de"];

// Exported for the key-parity unit test only — always read via `t()`.
export const STRINGS: Record<Locale, Record<string, string>> = {
  en: {
    "map.title": "Rent reductions in {city}",
    "map.titleCity": "Rent reductions in {city}",
    "map.titleZip": "Rent reductions in {zip}",
    "map.titleArea": "Rent reductions in this area",
    "map.titleView": "Rent reductions in this view",
    "map.casesInHouses": "Cases in {n} buildings",
    "map.totalPerMonth": "Total / month",
    "map.totalFromCases": "from {n} cases with recorded savings",
    "map.avgPerCase": "Avg. per case / month",
    "map.statusLine": "{closed} closed · {open} open",
    "map.legendTitle": "Legend",
    "map.legendClosed": "Won",
    "map.legendOpen": "In progress",
    "map.legendWon": "Successful cases",
    "map.legendSize": "Colour = case status",
    "map.searchPlaceholder":
      "Search an address or city, e.g. Berlin or Sonnenallee 99",
    "map.searchButton": "Search",
    "map.searchEmpty": "No matches",
    "map.searchedAddress": "Searched address",
    "map.resetTo": "Show all of {city}",
    "map.popupPerMonth": "{eur} / month",
    "map.popupAvgPerMonth": "Ø {eur} / month",
    "map.popupCases": "{n} {noun} · {closed} closed · {open} open",
    "map.popupCase": "case",
    "map.popupCasesPlural": "cases",
    "map.perMonthShort": "/ mo",
    "map.popupLandlord": "Landlord: {name}",
    "map.popupLastClosed": "Last closed: {year}",
    "map.loading": "Loading map…",
    "map.loadError": "Failed to load map data.",
  },
  de: {
    "map.title": "Mietminderungen in {city}",
    "map.titleCity": "Mietminderungen in {city}",
    "map.titleZip": "Mietminderungen in {zip}",
    "map.titleArea": "Mietminderungen in diesem Bereich",
    "map.titleView": "Mietminderungen in diesem Ausschnitt",
    "map.casesInHouses": "Fälle in {n} Häusern",
    "map.totalPerMonth": "Gesamt / Monat",
    "map.totalFromCases": "aus {n} Fällen mit erfasster Ersparnis",
    "map.avgPerCase": "Ø pro Fall / Monat",
    "map.statusLine": "{closed} abgeschlossen · {open} laufend",
    "map.legendTitle": "Legende",
    "map.legendClosed": "Mehrheitlich abgeschlossen",
    "map.legendOpen": "Mehrheitlich laufend",
    "map.legendWon": "Erfolgreiche Fälle",
    "map.legendSize": "Farbe = Fallstatus",
    "map.searchPlaceholder":
      "Adresse oder Stadt suchen, z. B. Berlin oder Sonnenallee 99",
    "map.searchButton": "Suchen",
    "map.searchEmpty": "Keine Treffer",
    "map.searchedAddress": "Gesuchte Adresse",
    "map.resetTo": "Ganz {city} anzeigen",
    "map.popupPerMonth": "{eur} € / Monat",
    "map.popupAvgPerMonth": "Ø {eur} € / Monat",
    "map.popupCases": "{n} {noun} · {closed} abgeschlossen · {open} laufend",
    "map.popupCase": "Fall",
    "map.popupCasesPlural": "Fälle",
    "map.perMonthShort": "/ Mt.",
    "map.popupLandlord": "Vermieter: {name}",
    "map.popupLastClosed": "Letzter Abschluss: {year}",
    "map.loading": "Karte wird geladen…",
    "map.loadError": "Kartendaten konnten nicht geladen werden.",
  },
};

function readFromLoaderScript(): Locale | "" {
  if (typeof document === "undefined") return "";
  const tags = document.querySelectorAll<HTMLScriptElement>(
    "script[data-conny-loader], script[src*='conny-widgets']",
  );
  for (const tag of Array.from(tags)) {
    const v = tag.getAttribute("data-conny-locale");
    if (v) {
      const norm = normalize(v);
      if (norm) return norm;
    }
  }
  return "";
}

function readFromHtmlLang(): Locale | "" {
  if (typeof document === "undefined") return "";
  return normalize(document.documentElement.lang || "");
}

function readFromNavigator(): Locale | "" {
  if (typeof navigator === "undefined") return "";
  return normalize(navigator.language || "");
}

function normalize(raw: string): Locale | "" {
  const head = String(raw || "")
    .toLowerCase()
    .split(/[-_]/)[0];
  if (head && (SUPPORTED as string[]).includes(head)) return head as Locale;
  return "";
}

/** Resolve the active locale using the documented precedence chain. */
export function resolveLocale(): Locale {
  return (
    normalize(readStorage(STORAGE_KEYS.locale)) ||
    readFromLoaderScript() ||
    readFromHtmlLang() ||
    readFromNavigator() ||
    "en"
  );
}

/** Look up a translation key with optional `{placeholder}` interpolation. */
export function t(
  key: string,
  vars: Record<string, string | number> = {},
  locale?: Locale,
): string {
  const loc = locale || resolveLocale();
  const table = STRINGS[loc] || STRINGS.en;
  const template = table[key] ?? STRINGS.en[key] ?? key;
  return template.replace(/\{(\w+)\}/g, (_, name) => {
    const v = vars[name];
    return v == null ? "" : String(v);
  });
}

/** React hook that re-renders when the active locale changes. */
export function useLocale(): Locale {
  const [stored] = useStorage(STORAGE_KEYS.locale);
  return normalize(stored) || resolveLocale();
}
